/**
 * Hero Slideshow Section
 * Handles auto-rotation, progress bar animation, video playback, and keyboard navigation
 * ADA compliant with proper ARIA attributes and reduced motion support
 */

class HeroSlideshow extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.isPlaying = true;
    this.autoplayInterval = null;
    this.wasPlayingBeforeHover = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  connectedCallback() {
    this.slides = this.querySelectorAll('.hero-slideshow__slide');
    this.progressItems = this.querySelectorAll('.hero-slideshow__progress-item');
    this.prevButton = this.querySelector('.hero-slideshow__arrow--prev');
    this.nextButton = this.querySelector('.hero-slideshow__arrow--next');
    this.pauseButton = this.querySelector('.hero-slideshow__pause');
    this.slidesContainer = this.querySelector('.hero-slideshow__slides');

    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 6000;
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';

    if (this.slides.length <= 1) return;

    this.bindEvents();
    this.initializeVideos();

    if (this.autoplay && !this.reducedMotion) {
      this.startAutoplay();
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  bindEvents() {
    // Progress bar clicks
    this.progressItems.forEach((item, index) => {
      item.addEventListener('click', () => this.goToSlide(index));
    });

    // Arrow navigation
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.prevSlide());
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.nextSlide());
    }

    // Pause/play button
    if (this.pauseButton) {
      this.pauseButton.addEventListener('click', () => this.toggleAutoplay());
    }

    // Keyboard navigation
    this.addEventListener('keydown', (e) => this.handleKeydown(e));

    // Pause on hover
    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => {
        if (this.isPlaying) {
          this.pauseAutoplay();
          this.wasPlayingBeforeHover = true;
        }
      });

      this.addEventListener('mouseleave', () => {
        if (this.wasPlayingBeforeHover) {
          this.resumeAutoplay();
          this.wasPlayingBeforeHover = false;
        }
      });
    }

    // Handle visibility change (pause when tab not visible)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAutoplay();
        this.pauseAllVideos();
      } else if (this.isPlaying) {
        this.resumeAutoplay();
        this.playActiveSlideVideo();
      }
    });

    // Intersection Observer for performance (pause when not in view)
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            this.pauseAutoplay();
            this.pauseAllVideos();
          } else if (this.isPlaying) {
            this.resumeAutoplay();
            this.playActiveSlideVideo();
          }
        });
      },
      { threshold: 0.25 }
    );
    observer.observe(this);

    // Listen for reduced motion preference changes
    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
      this.reducedMotion = e.matches;
      if (this.reducedMotion) {
        this.stopAutoplay();
        this.pauseAllVideos();
      }
    });
  }

  initializeVideos() {
    // Pause all videos initially except the active slide
    this.slides.forEach((slide, index) => {
      const videos = slide.querySelectorAll('video');
      videos.forEach((video) => {
        if (index === 0 && !this.reducedMotion) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    });
  }

  handleKeydown(e) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.prevSlide();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.nextSlide();
    } else if (e.key === ' ' || e.key === 'Spacebar') {
      // Only toggle if focus is on the slideshow container itself or pause button
      if (e.target === this || e.target.classList.contains('hero-slideshow__pause')) {
        e.preventDefault();
        this.toggleAutoplay();
      }
    } else if (e.key === 'Home') {
      e.preventDefault();
      this.goToSlide(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      this.goToSlide(this.slides.length - 1);
    }
  }

  goToSlide(index) {
    if (index === this.currentIndex) return;

    const previousIndex = this.currentIndex;

    // Pause video on previous slide
    const previousSlide = this.slides[previousIndex];
    if (previousSlide) {
      const previousVideos = previousSlide.querySelectorAll('video');
      previousVideos.forEach((video) => video.pause());
    }

    // Update slides
    this.slides.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle('hero-slideshow__slide--active', isActive);
      slide.setAttribute('aria-hidden', !isActive);
    });

    // Update progress items
    this.progressItems.forEach((item, i) => {
      const isActive = i === index;
      item.classList.toggle('hero-slideshow__progress-item--active', isActive);
      item.setAttribute('aria-selected', isActive);

      // Reset progress fill animation
      const fill = item.querySelector('.hero-slideshow__progress-fill');
      if (fill) {
        fill.style.animation = 'none';
        fill.offsetHeight; // Trigger reflow
        fill.style.animation = '';
      }
    });

    this.currentIndex = index;

    // Play video on new active slide
    if (!this.reducedMotion) {
      this.playActiveSlideVideo();
    }

    // Reset autoplay timer
    if (this.isPlaying && this.autoplay) {
      this.stopAutoplay();
      this.startAutoplay();
    }

    // Announce slide change to screen readers
    this.announceSlideChange(index);
  }

  playActiveSlideVideo() {
    const activeSlide = this.slides[this.currentIndex];
    if (activeSlide) {
      const videos = activeSlide.querySelectorAll('video');
      videos.forEach((video) => {
        video.currentTime = 0;
        video.play().catch(() => {});
      });
    }
  }

  pauseAllVideos() {
    this.slides.forEach((slide) => {
      const videos = slide.querySelectorAll('video');
      videos.forEach((video) => video.pause());
    });
  }

  nextSlide() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }

  prevSlide() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(prevIndex);
  }

  startAutoplay() {
    if (this.reducedMotion) return;

    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
    this.classList.remove('hero-slideshow--paused');
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  pauseAutoplay() {
    this.stopAutoplay();
    this.classList.add('hero-slideshow--paused');
  }

  resumeAutoplay() {
    if (this.autoplay && this.isPlaying && !this.reducedMotion) {
      this.startAutoplay();
    }
  }

  toggleAutoplay() {
    this.isPlaying = !this.isPlaying;

    if (this.pauseButton) {
      this.pauseButton.setAttribute('data-playing', this.isPlaying);
      const pauseLabel = this.pauseButton.dataset.pauseLabel || 'Pause slideshow';
      const playLabel = this.pauseButton.dataset.playLabel || 'Play slideshow';
      this.pauseButton.setAttribute('aria-label', this.isPlaying ? pauseLabel : playLabel);
    }

    if (this.isPlaying) {
      this.startAutoplay();
      this.playActiveSlideVideo();
    } else {
      this.pauseAutoplay();
      this.pauseAllVideos();
    }
  }

  announceSlideChange(index) {
    // Create or update live region for screen readers
    let liveRegion = this.querySelector('.hero-slideshow__live-region');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'hero-slideshow__live-region';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.style.cssText =
        'position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0);';
      this.appendChild(liveRegion);
    }
    liveRegion.textContent = `Slide ${index + 1} of ${this.slides.length}`;
  }
}

// Register custom element if not already registered
if (!customElements.get('hero-slideshow')) {
  customElements.define('hero-slideshow', HeroSlideshow);
}

// Initialize slideshows that aren't using custom elements (fallback)
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.hero-slideshow:not(hero-slideshow)').forEach((slideshow) => {
    initSlideshow(slideshow);
  });
});

function initSlideshow(container) {
  const slides = container.querySelectorAll('.hero-slideshow__slide');
  const progressItems = container.querySelectorAll('.hero-slideshow__progress-item');
  const prevButton = container.querySelector('.hero-slideshow__arrow--prev');
  const nextButton = container.querySelector('.hero-slideshow__arrow--next');
  const pauseButton = container.querySelector('.hero-slideshow__pause');

  if (slides.length <= 1) return;

  let currentIndex = 0;
  let isPlaying = true;
  let autoplayInterval = null;
  let wasPlayingBeforeHover = false;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const autoplay = container.dataset.autoplay === 'true';
  const autoplaySpeed = parseInt(container.dataset.autoplaySpeed, 10) || 6000;
  const pauseOnHover = container.dataset.pauseOnHover === 'true';

  function initializeVideos() {
    slides.forEach((slide, index) => {
      const videos = slide.querySelectorAll('video');
      videos.forEach((video) => {
        if (index === 0 && !reducedMotion) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    });
  }

  function playActiveSlideVideo() {
    if (reducedMotion) return;
    const activeSlide = slides[currentIndex];
    if (activeSlide) {
      const videos = activeSlide.querySelectorAll('video');
      videos.forEach((video) => {
        video.currentTime = 0;
        video.play().catch(() => {});
      });
    }
  }

  function pauseAllVideos() {
    slides.forEach((slide) => {
      const videos = slide.querySelectorAll('video');
      videos.forEach((video) => video.pause());
    });
  }

  function goToSlide(index) {
    if (index === currentIndex) return;

    // Pause previous slide videos
    const previousSlide = slides[currentIndex];
    if (previousSlide) {
      const videos = previousSlide.querySelectorAll('video');
      videos.forEach((video) => video.pause());
    }

    slides.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle('hero-slideshow__slide--active', isActive);
      slide.setAttribute('aria-hidden', !isActive);
    });

    progressItems.forEach((item, i) => {
      const isActive = i === index;
      item.classList.toggle('hero-slideshow__progress-item--active', isActive);
      item.setAttribute('aria-selected', isActive);

      const fill = item.querySelector('.hero-slideshow__progress-fill');
      if (fill) {
        fill.style.animation = 'none';
        fill.offsetHeight;
        fill.style.animation = '';
      }
    });

    currentIndex = index;
    playActiveSlideVideo();

    if (isPlaying && autoplay) {
      stopAutoplay();
      startAutoplay();
    }
  }

  function nextSlide() {
    const nextIndex = (currentIndex + 1) % slides.length;
    goToSlide(nextIndex);
  }

  function prevSlide() {
    const prevIndex = (currentIndex - 1 + slides.length) % slides.length;
    goToSlide(prevIndex);
  }

  function startAutoplay() {
    if (reducedMotion) return;
    stopAutoplay();
    autoplayInterval = setInterval(nextSlide, autoplaySpeed);
    container.classList.remove('hero-slideshow--paused');
  }

  function stopAutoplay() {
    if (autoplayInterval) {
      clearInterval(autoplayInterval);
      autoplayInterval = null;
    }
  }

  function pauseAutoplay() {
    stopAutoplay();
    container.classList.add('hero-slideshow--paused');
  }

  function toggleAutoplay() {
    isPlaying = !isPlaying;

    if (pauseButton) {
      pauseButton.setAttribute('data-playing', isPlaying);
      const pauseLabel = pauseButton.dataset.pauseLabel || 'Pause slideshow';
      const playLabel = pauseButton.dataset.playLabel || 'Play slideshow';
      pauseButton.setAttribute('aria-label', isPlaying ? pauseLabel : playLabel);
    }

    if (isPlaying) {
      startAutoplay();
      playActiveSlideVideo();
    } else {
      pauseAutoplay();
      pauseAllVideos();
    }
  }

  // Initialize
  initializeVideos();

  // Bind events
  progressItems.forEach((item, index) => {
    item.addEventListener('click', () => goToSlide(index));
  });

  if (prevButton) prevButton.addEventListener('click', prevSlide);
  if (nextButton) nextButton.addEventListener('click', nextSlide);
  if (pauseButton) pauseButton.addEventListener('click', toggleAutoplay);

  if (pauseOnHover) {
    container.addEventListener('mouseenter', () => {
      if (isPlaying) {
        pauseAutoplay();
        wasPlayingBeforeHover = true;
      }
    });
    container.addEventListener('mouseleave', () => {
      if (wasPlayingBeforeHover) {
        startAutoplay();
        wasPlayingBeforeHover = false;
      }
    });
  }

  container.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      prevSlide();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      nextSlide();
    } else if (e.key === ' ' || e.key === 'Spacebar') {
      if (e.target === container || e.target.classList.contains('hero-slideshow__pause')) {
        e.preventDefault();
        toggleAutoplay();
      }
    }
  });

  // Visibility change
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      pauseAutoplay();
      pauseAllVideos();
    } else if (isPlaying) {
      startAutoplay();
      playActiveSlideVideo();
    }
  });

  // Intersection observer
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          pauseAutoplay();
          pauseAllVideos();
        } else if (isPlaying) {
          startAutoplay();
          playActiveSlideVideo();
        }
      });
    },
    { threshold: 0.25 }
  );
  observer.observe(container);

  if (autoplay && !reducedMotion) {
    startAutoplay();
  }
}
